import { createColumnHelper } from '@tanstack/react-table';
import { RoleGetResult } from '@mews-commander/data-access';
import { LocalizedTextTag, TooltippedSpan } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { compactArray } from '@mews/fp';
import { ColumnFilterType, Link } from '@optimus-web/core';
import { useRoleContext } from '../context';
import { usePortfolioSelectors } from '../utils';
import { PrivilegesCount } from './PrivilegesCount';
import { RolesTableActions } from './RolesTableActions';

interface UseColumnsOptions {
    onRowDelete: (id: string) => void;
}

const columnHelper = createColumnHelper<RoleGetResult>();

export const useColumns = ({ onRowDelete }: UseColumnsOptions) => {
    const { isPortfolio, isInsidePortfolio, hasMultiPropertyAccess } = usePortfolioSelectors();
    const { openRoleDetail } = useRoleContext();

    return compactArray([
        columnHelper.accessor('name', {
            header: () => <LocalizedText textKey={TextKey.RoleName} />,
            cell: (props) => <RoleName role={props.row.original} />,
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
        }),
        isInsidePortfolio && !isPortfolio
            ? columnHelper.accessor('isLocal', {
                  header: () => <LocalizedText textKey={TextKey.Type} />,
                  cell: (props) => {
                      const isLocal = props.getValue();
                      return (
                          <TooltippedSpan
                              tooltip={
                                  <LocalizedText
                                      textKey={isLocal ? TextKey.RoleScopeLocalHint : TextKey.RoleScopeGlobalHint}
                                  />
                              }
                              tooltipDelay={300}
                          >
                              <LocalizedTextTag
                                  variant="basicSubtle"
                                  textKey={isLocal ? TextKey.RoleScopeLocal : TextKey.RoleScopeGlobal}
                              />
                          </TooltippedSpan>
                      );
                  },
              })
            : null,
        columnHelper.accessor('privileges', {
            meta: { align: 'end' },
            header: () => <LocalizedText textKey={TextKey.Privileges} />,
            cell: (props) => <PrivilegesCount privileges={props.getValue()} />,
        }),
        isPortfolio
            ? columnHelper.display({
                  id: 'properties',
                  meta: { align: 'end' },
                  header: () => <LocalizedText textKey={TextKey.RolePropertiesCount} />,
                  cell: () => <LocalizedText textKey={TextKey.All} />,
              })
            : null,
        isPortfolio && hasMultiPropertyAccess
            ? columnHelper.accessor('profileCount', {
                  meta: { align: 'end' },
                  header: () => <LocalizedText textKey={TextKey.RoleUsers} />,
                  cell: (props) => {
                      const value = props.getValue() ?? 0;

                      return (
                          <Link
                              variant="basic"
                              onClick={() => openRoleDetail({ role: props.row.original, users: true })}
                          >
                              {value}
                          </Link>
                      );
                  },
              })
            : null,
        columnHelper.display({
            id: 'actions',
            meta: { width: 100, align: 'end' },
            cell: (props) => <RolesTableActions role={props.row.original} onRowDelete={onRowDelete} />,
        }),
    ]);
};

const RoleName = ({ role }: { role: RoleGetResult }) => {
    const { openRoleDetail } = useRoleContext();

    return (
        <Link variant="basic" onClick={() => openRoleDetail({ role })}>
            {role.name}
        </Link>
    );
};
